import { matchPath } from "react-router";
import { appPaths, ROUTE_PATTERNS } from "../../../shared/routing/appPaths";

/**
 * The Data model page has one navigation level, as v1's (F77): the terms of a model version
 * at the page's own address, with their kinds as its one row. The version's checks are its
 * status in the header, a chip that opens their panel, and its releases a list the header
 * opens (design pass section 9); one release has a page of its own, and one extraction result
 * its page at its deep-link address (H-21). The regests moved to Documents (F64, P-07).
 */
export type DataModelPage = "terms" | "result" | "release";

/** The pages that show one model version, so the header's version picker applies to them. */
export type VersionedPage = Extract<DataModelPage, "terms">;

/** The pages with a fixed path segment; the terms are the page's own address and a version below it. */
const FIXED_PAGE_PATTERNS = [
  ["result", ROUTE_PATTERNS.extraction],
  ["release", ROUTE_PATTERNS.release],
] as const satisfies readonly (readonly [DataModelPage, string])[];

/** The page a path shows. */
export function dataModelPageOfPath(pathname: string): DataModelPage {
  return FIXED_PAGE_PATTERNS.find(([, path]) => matchPath({ path, end: false }, pathname) !== null)?.[0] ?? "terms";
}

/** Whether the path names one extraction result or one release: those pages read their own
 * resource, so they show without a chosen project. */
export function namesOneResource(pathname: string): boolean {
  return [ROUTE_PATTERNS.extraction, ROUTE_PATTERNS.release].some((path) => matchPath(path, pathname) !== null);
}

export function pageShowsModelVersion(page: DataModelPage): page is VersionedPage {
  return page === "terms";
}

/** Where a page that shows a model version opens, at the chosen one, if any. */
export function versionedPagePath(_page: VersionedPage, version: string | null): string {
  return version === null ? appPaths.ontology() : appPaths.ontology({ version });
}
